import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import PropertyCard from '@/components/PropertyCard';
import { useAuth } from '@/contexts/AuthContext';
import { getMyProperties, getSavedProperties, getInquiries, deleteProperty, toggleSaveProperty, getAIRecommendations } from '@/lib/api';
import { toast } from 'sonner';
import UserBookings from '@/components/UserBookings';
import SavedSearches from '@/components/SavedSearches';
import { 
  Building2, Heart, MessageSquare, Settings, PlusCircle, Trash2, Edit, Eye,
  LayoutDashboard, Mail, Phone, User, Sparkles, AlertCircle, FileText, Download, Bell
} from 'lucide-react';

const DashboardPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, updateProfile } = useAuth();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [myProperties, setMyProperties] = useState([]);
  const [savedProperties, setSavedProperties] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savedIds, setSavedIds] = useState(new Set());
  
  // Profile form
  const [profileForm, setProfileForm] = useState({
    name: '',
    phone: '',
    user_type: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);

  // AI Recommendations
  const [aiPreferences, setAiPreferences] = useState('');
  const [aiBudgetMin, setAiBudgetMin] = useState('');
  const [aiBudgetMax, setAiBudgetMax] = useState('');
  const [aiCity, setAiCity] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);

  useEffect(() => {
    if (user) {
      setProfileForm({
        name: user.name || '',
        phone: user.phone || '',
        user_type: user.user_type || 'buyer'
      });
    }
    loadData();
  }, [user]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [myPropsData, savedData, inquiriesData] = await Promise.all([
        getMyProperties(),
        getSavedProperties(),
        getInquiries()
      ]);
      setMyProperties(myPropsData);
      setSavedProperties(savedData);
      setInquiries(inquiriesData);
      setSavedIds(new Set(savedData.map(p => p.property_id)));
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    if (!window.confirm('Are you sure you want to delete this property?')) return;
    
    try {
      await deleteProperty(propertyId);
      setMyProperties(prev => prev.filter(p => p.property_id !== propertyId));
      toast.success('Property deleted');
    } catch (error) {
      toast.error('Failed to delete property');
    }
  };

  const handleUnsave = async (propertyId) => {
    try {
      await toggleSaveProperty(propertyId);
      setSavedProperties(prev => prev.filter(p => p.property_id !== propertyId));
      setSavedIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(propertyId);
        return newSet;
      });
      toast.info('Property removed from saved');
    } catch (error) {
      toast.error('Failed to remove property');
    }
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await updateProfile(profileForm);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleAIRecommendations = async () => {
    if (!aiPreferences) {
      toast.error('Please describe your preferences');
      return;
    }

    setAiLoading(true);
    try {
      const result = await getAIRecommendations({
        preferences: aiPreferences,
        budget_min: aiBudgetMin ? parseFloat(aiBudgetMin) : undefined,
        budget_max: aiBudgetMax ? parseFloat(aiBudgetMax) : undefined,
        city: aiCity || undefined
      });
      setAiResult(result);
    } catch (error) {
      toast.error('Failed to get recommendations');
    } finally {
      setAiLoading(false);
    }
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    setSearchParams({ tab: value });
  };

  const stats = [
    { label: 'Listed Properties', value: myProperties.length, icon: Building2 },
    { label: 'Saved Properties', value: savedProperties.length, icon: Heart },
    { label: 'Total Inquiries', value: inquiries.length, icon: MessageSquare },
    { label: 'Total Views', value: myProperties.reduce((acc, p) => acc + (p.views || 0), 0), icon: Eye },
  ];

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-16">
      <div className="container-luxury">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-['Plus_Jakarta_Sans'] text-3xl font-bold text-[#0F172A]">Dashboard</h1>
            <p className="text-muted-foreground">Welcome back, {user?.name}</p>
          </div>
          <Link to="/add-property">
            <Button className="btn-primary" data-testid="add-property-btn">
              <PlusCircle className="h-4 w-4 mr-2" /> List New Property
            </Button>
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white p-6 rounded-sm shadow-sm border border-border/50">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-sm bg-[#F1F5F9] flex items-center justify-center">
                  <stat.icon className="h-6 w-6 text-[#0F172A]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0F172A]">{stat.value}</p>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <TabsList className="bg-white p-1 border">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#0F172A] data-[state=active]:text-white" data-testid="tab-overview">
              <LayoutDashboard className="h-4 w-4 mr-2" /> Overview
            </TabsTrigger>
            <TabsTrigger value="properties" className="data-[state=active]:bg-[#0F172A] data-[state=active]:text-white" data-testid="tab-properties">
              <Building2 className="h-4 w-4 mr-2" /> My Properties
            </TabsTrigger>
            <TabsTrigger value="saved" className="data-[state=active]:bg-[#0F172A] data-[state=active]:text-white" data-testid="tab-saved">
              <Heart className="h-4 w-4 mr-2" /> Saved
            </TabsTrigger>
            <TabsTrigger value="saved-searches" className="data-[state=active]:bg-[#0F172A] data-[state=active]:text-white" data-testid="tab-saved-searches">
              <Bell className="h-4 w-4 mr-2" /> Saved Searches
            </TabsTrigger>
            <TabsTrigger value="inquiries" className="data-[state=active]:bg-[#0F172A] data-[state=active]:text-white" data-testid="tab-inquiries">
              <MessageSquare className="h-4 w-4 mr-2" /> Inquiries
            </TabsTrigger>
            <TabsTrigger value="ai" className="data-[state=active]:bg-[#0F172A] data-[state=active]:text-white" data-testid="tab-ai">
              <Sparkles className="h-4 w-4 mr-2" /> AI Advisor
            </TabsTrigger>
            <TabsTrigger value="invoices" className="data-[state=active]:bg-[#0F172A] data-[state=active]:text-white" data-testid="tab-invoices">
              <FileText className="h-4 w-4 mr-2" /> Invoices
            </TabsTrigger>
            <TabsTrigger value="bookings" className="data-[state=active]:bg-[#0F172A] data-[state=active]:text-white" data-testid="tab-bookings">
              <Sparkles className="h-4 w-4 mr-2" /> My Bookings
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-[#0F172A] data-[state=active]:text-white" data-testid="tab-settings">
              <Settings className="h-4 w-4 mr-2" /> Settings
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Recent Properties */}
              <div className="bg-white p-6 rounded-sm shadow-sm border border-border/50">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-['Plus_Jakarta_Sans'] text-xl font-semibold text-[#0F172A]">Recent Listings</h2>
                  <Link to="?tab=properties" className="text-sm text-[#D4AF37] hover:underline">View all</Link>
                </div>
                {myProperties.length > 0 ? (
                  <div className="space-y-4">
                    {myProperties.slice(0, 3).map((prop) => (
                      <div key={prop.property_id} className="flex items-center gap-4 p-3 bg-[#F9F9F7] rounded-sm">
                        <img
                          src={prop.images?.[0] || 'https://images.unsplash.com/photo-1629946488804-217c002178cf?w=100'}
                          alt={prop.title}
                          className="h-16 w-16 rounded-sm object-cover"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-[#0F172A] truncate">{prop.title}</h4>
                          <p className="text-sm text-muted-foreground">{prop.views} views • {prop.inquiries} inquiries</p>
                        </div>
                        <Link to={`/properties/${prop.property_id}`}>
                          <Button variant="ghost" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </Link>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Building2 className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
                    <p className="text-muted-foreground mb-4">No properties listed yet</p>
                    <Link to="/add-property">
                      <Button size="sm">List Your First Property</Button>
                    </Link>
                  </div>
                )}
              </div>

              {/* Recent Inquiries */}
              <div className="bg-white p-6 rounded-sm shadow-sm border border-border/50">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-['Plus_Jakarta_Sans'] text-xl font-semibold text-[#0F172A]">Recent Inquiries</h2>
                  <Link to="?tab=inquiries" className="text-sm text-[#D4AF37] hover:underline">View all</Link>
                </div>
                {inquiries.length > 0 ? (
                  <div className="space-y-4">
                    {inquiries.slice(0, 4).map((inq) => (
                      <div key={inq.inquiry_id} className="flex items-start gap-3 p-3 bg-[#F9F9F7] rounded-sm">
                        <div className="h-10 w-10 rounded-full bg-[#0F172A] flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-sm">{inq.name?.[0]?.toUpperCase()}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#0F172A]">{inq.name}</p>
                          <p className="text-sm text-muted-foreground truncate">{inq.message}</p>
                          <p className="text-xs text-muted-foreground mt-1">for {inq.property_title}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
                    <p className="text-muted-foreground">No inquiries yet</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* My Properties Tab */}
          <TabsContent value="properties">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white rounded-sm animate-pulse">
                    <div className="aspect-[4/3] bg-gray-200" />
                    <div className="p-5 space-y-3">
                      <div className="h-5 bg-gray-200 rounded w-3/4" />
                      <div className="h-4 bg-gray-200 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : myProperties.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {myProperties.map((property) => (
                  <div key={property.property_id} className="relative">
                    <PropertyCard property={property} showSaveButton={false} />
                    <div className="absolute top-4 right-4 flex gap-2">
                      <Link to={`/edit-property/${property.property_id}`}>
                        <Button size="icon" variant="secondary" className="h-8 w-8" data-testid={`edit-${property.property_id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                      </Link>
                      <Button
                        size="icon"
                        variant="destructive"
                        className="h-8 w-8"
                        onClick={() => handleDeleteProperty(property.property_id)}
                        data-testid={`delete-${property.property_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20 bg-white rounded-sm">
                <Building2 className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="text-xl font-medium text-[#0F172A] mb-2">No Properties Listed</h3>
                <p className="text-muted-foreground mb-6">Start listing your properties to reach potential buyers</p>
                <Link to="/add-property">
                  <Button className="btn-primary">List Your First Property</Button>
                </Link>
              </div>
            )}
          </TabsContent>

          {/* Saved Tab */}
          <TabsContent value="saved">
            {savedProperties.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {savedProperties.map((property) => (
                  <PropertyCard
                    key={property.property_id}
                    property={property}
                    onSave={handleUnsave}
                    isSaved={true}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20 bg-white rounded-sm">
                <Heart className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="text-xl font-medium text-[#0F172A] mb-2">No Saved Properties</h3>
                <p className="text-muted-foreground mb-6">Save properties you like for quick access later</p>
                <Link to="/properties">
                  <Button className="btn-primary">Browse Properties</Button>
                </Link>
              </div>
            )}
          </TabsContent>

          {/* Saved Searches Tab */}
          <TabsContent value="saved-searches">
            <SavedSearches />
          </TabsContent>

          {/* Inquiries Tab */}
          <TabsContent value="inquiries">
            {inquiries.length > 0 ? (
              <div className="bg-white rounded-sm shadow-sm border border-border/50 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-[#F9F9F7]">
                        <th className="text-left p-4 font-medium text-[#0F172A]">Name</th>
                        <th className="text-left p-4 font-medium text-[#0F172A]">Contact</th>
                        <th className="text-left p-4 font-medium text-[#0F172A]">Property</th>
                        <th className="text-left p-4 font-medium text-[#0F172A]">Message</th>
                        <th className="text-left p-4 font-medium text-[#0F172A]">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inquiries.map((inq) => (
                        <tr key={inq.inquiry_id} className="border-b last:border-0 hover:bg-[#F9F9F7]">
                          <td className="p-4 font-medium">{inq.name}</td>
                          <td className="p-4">
                            <div className="space-y-1">
                              <p className="text-sm flex items-center gap-1">
                                <Mail className="h-3 w-3" /> {inq.email}
                              </p>
                              <p className="text-sm flex items-center gap-1">
                                <Phone className="h-3 w-3" /> {inq.phone}
                              </p>
                            </div>
                          </td>
                          <td className="p-4">
                            <Link to={`/properties/${inq.property_id}`} className="text-[#D4AF37] hover:underline">
                              {inq.property_title}
                            </Link>
                          </td>
                          <td className="p-4 max-w-xs truncate">{inq.message}</td>
                          <td className="p-4 text-sm text-muted-foreground">
                            {new Date(inq.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-20 bg-white rounded-sm">
                <MessageSquare className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="text-xl font-medium text-[#0F172A] mb-2">No Inquiries Yet</h3>
                <p className="text-muted-foreground">When someone inquires about your property, it will appear here</p>
              </div>
            )}
          </TabsContent>

          {/* AI Advisor Tab */}
          <TabsContent value="ai">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-sm shadow-sm border border-border/50">
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-12 w-12 rounded-sm bg-[#D4AF37] flex items-center justify-center">
                    <Sparkles className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="font-['Plus_Jakarta_Sans'] text-xl font-semibold text-[#0F172A]">AI Property Advisor</h2>
                    <p className="text-sm text-muted-foreground">Get personalized property recommendations</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label>Describe your ideal property</Label>
                    <Textarea
                      placeholder="e.g., Looking for a 3BHK apartment near IT parks with good schools nearby, modern amenities, and within 20 minutes from the city center..."
                      value={aiPreferences}
                      onChange={(e) => setAiPreferences(e.target.value)}
                      rows={4}
                      className="mt-2"
                      data-testid="ai-preferences"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Min Budget (₹)</Label>
                      <Input
                        type="number"
                        placeholder="e.g., 5000000"
                        value={aiBudgetMin}
                        onChange={(e) => setAiBudgetMin(e.target.value)}
                        className="mt-2"
                        data-testid="ai-budget-min"
                      />
                    </div>
                    <div>
                      <Label>Max Budget (₹)</Label>
                      <Input
                        type="number"
                        placeholder="e.g., 15000000"
                        value={aiBudgetMax}
                        onChange={(e) => setAiBudgetMax(e.target.value)}
                        className="mt-2"
                        data-testid="ai-budget-max"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Preferred City</Label>
                    <Input
                      placeholder="e.g., Mumbai, Bangalore"
                      value={aiCity}
                      onChange={(e) => setAiCity(e.target.value)}
                      className="mt-2"
                      data-testid="ai-city"
                    />
                  </div>

                  <Button
                    onClick={handleAIRecommendations}
                    disabled={aiLoading}
                    className="w-full btn-gold"
                    data-testid="get-ai-recommendations"
                  >
                    {aiLoading ? 'Analyzing...' : 'Get AI Recommendations'}
                  </Button>
                </div>
              </div>

              <div className="bg-white p-8 rounded-sm shadow-sm border border-border/50">
                <h3 className="font-medium text-[#0F172A] mb-4">AI Analysis</h3>
                {aiResult ? (
                  <div className="space-y-6">
                    <div className="p-4 bg-[#F9F9F7] rounded-sm">
                      <p className="text-muted-foreground whitespace-pre-line">{aiResult.ai_analysis}</p>
                    </div>
                    {aiResult.recommendations?.length > 0 && (
                      <div>
                        <h4 className="font-medium text-[#0F172A] mb-3">Recommended Properties</h4>
                        <div className="space-y-3">
                          {aiResult.recommendations.map((prop) => (
                            <Link
                              key={prop.property_id}
                              to={`/properties/${prop.property_id}`}
                              className="flex items-center gap-3 p-3 bg-[#F9F9F7] rounded-sm hover:bg-[#E2E8F0] transition-colors"
                            >
                              <img
                                src={prop.images?.[0] || 'https://images.unsplash.com/photo-1629946488804-217c002178cf?w=100'}
                                alt={prop.title}
                                className="h-12 w-12 rounded-sm object-cover"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-[#0F172A] truncate">{prop.title}</p>
                                <p className="text-sm text-muted-foreground">{prop.city}</p>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Sparkles className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
                    <p className="text-muted-foreground">Describe your preferences to get personalized recommendations</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices" data-testid="invoices-panel">
            <UserInvoices />
          </TabsContent>

          {/* Service Bookings Tab */}
          <TabsContent value="bookings" data-testid="bookings-panel">
            <UserBookings />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="max-w-2xl">
              <div className="bg-white p-8 rounded-sm shadow-sm border border-border/50">
                <h2 className="font-['Plus_Jakarta_Sans'] text-xl font-semibold text-[#0F172A] mb-6">Profile Settings</h2>
                
                <form onSubmit={handleProfileSave} className="space-y-6">
                  <div>
                    <Label htmlFor="name">Full Name</Label>
                    <div className="relative mt-2">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                      <Input
                        id="name"
                        value={profileForm.name}
                        onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                        className="h-12 pl-10"
                        data-testid="settings-name"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email">Email</Label>
                    <div className="relative mt-2">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                      <Input
                        id="email"
                        value={user?.email || ''}
                        disabled
                        className="h-12 pl-10 bg-gray-50"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Email cannot be changed</p>
                  </div>

                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <div className="relative mt-2">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                      <Input
                        id="phone"
                        value={profileForm.phone}
                        onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                        placeholder="+91 94650 24516"
                        className="h-12 pl-10"
                        data-testid="settings-phone"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="user_type">I am a</Label>
                    <Select
                      value={profileForm.user_type}
                      onValueChange={(v) => setProfileForm({ ...profileForm, user_type: v })}
                    >
                      <SelectTrigger className="h-12 mt-2" data-testid="settings-user-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="buyer">Buyer / Tenant</SelectItem>
                        <SelectItem value="seller">Property Owner</SelectItem>
                        <SelectItem value="agent">Real Estate Agent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button type="submit" className="btn-primary" disabled={savingProfile} data-testid="save-profile-btn">
                    {savingProfile ? 'Saving...' : 'Save Changes'}
                  </Button>
                </form>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const UserInvoices = () => {
  const [invoices, setInvoices] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/me/invoices`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const d = await res.json();
          setInvoices(d.invoices || []);
        }
      } catch { /* noop */ }
      setLoading(false);
    };
    load();
  }, []);

  const download = async (num) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/me/invoices/download?invoice_number=${encodeURIComponent(num)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { toast.error('Download failed'); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${num.replace(/\//g, '-')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const formatINR = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

  return (
    <div className="bg-white p-6 md:p-8 rounded-sm shadow-sm border border-border/50">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-['Plus_Jakarta_Sans'] text-xl font-semibold text-[#0F172A]">Your Invoices</h2>
        <span className="text-xs text-muted-foreground">{invoices.length} {invoices.length === 1 ? 'invoice' : 'invoices'}</span>
      </div>
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading…</div>
      ) : invoices.length === 0 ? (
        <div className="text-center py-12 border border-dashed rounded-sm">
          <FileText className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">No invoices yet. Purchase a premium plan to see invoices here.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground uppercase border-b">
              <tr>
                <th className="text-left py-2">Invoice #</th>
                <th className="text-left py-2">Plan</th>
                <th className="text-right py-2">Amount</th>
                <th className="text-left py-2">Date</th>
                <th className="text-right py-2"></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.invoice_number} className="border-b border-border/30 hover:bg-[#F8FAFC]">
                  <td className="py-3 font-mono text-xs">{inv.invoice_number}</td>
                  <td className="py-3 capitalize">{inv.plan_name}</td>
                  <td className="py-3 text-right font-semibold">{formatINR(inv.total_amount)}</td>
                  <td className="py-3 text-xs text-muted-foreground">{new Date(inv.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                  <td className="py-3 text-right">
                    <Button onClick={() => download(inv.invoice_number)} size="sm" variant="outline" className="gap-1.5" data-testid={`my-invoice-download-${inv.invoice_number}`}>
                      <Download className="h-3.5 w-3.5" /> PDF
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
